<script setup>
//#region import
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useError } from '@/composables/useError';
import eventBus from '@/setup/eventBus';
//#endregion

//#region props
const props = defineProps({
  label: {
    type: String,
    default: () => '',
  },
  labelSize: {
    type: Number,
    default: () => 14,
  },
  labelBold: {
    type: Boolean,
    default: () => false,
  },
  text: {
    type: String,
    default: () => '',
  },
  placeholder: {
    type: String,
    default: () => '',
  },
  disabled: {
    type: Boolean,
    default: () => false,
  },
  rows: {
    type: Number,
    default: () => 3,
  },
  resize: {
    type: String,
    default: () => 'none',
  },
  error: {
    type: [String, Number, Array],
    default: () => '',
  },
  isError: {
    type: Boolean,
    default: () => false,
  },
  required: {
    type: Boolean,
    default: () => false,
  },
  autosize: {
    type: [Boolean, Object],
    default: () => false,
  },
  readonly: {
    type: Boolean,
    default: () => false,
  },
  isCopy: {
    type: Boolean,
    default: () => false,
  },
  isClear: {
    type: Boolean,
    default: () => false,
  },
  maxlength: {
    type:[String, Number],
    default: () => '',
  },
});
//#endregion

const emit = defineEmits(['blur', 'error', 'focus']);

const { t } = useI18n();

//#region data
const modelValue = defineModel({ type: String });
const textareaRef = ref(null);

const { currentError } = useError({
  error: computed(() => {
    return props.error;
  }),
});

function focus() {
  textareaRef.value?.focus();
}

function handleFocus(event) {
  emit('focus', event);
}

function handleBlur(event) {
  if (props.required && !event.target.value) {
    emit('error', currentError.value);
  }
  emit('blur', event);
}

function onCopy() {
  eventBus.$emit('notify-event', {
    type: 'info',
    text: t('domainManagement.messages.copied_to_clipboard'),
  });
}

function onError() {
  eventBus.$emit('notify-event', {
    type: 'error',
    text: t('domainManagement.messages.copy_error'),
  });
}

defineExpose({
  focus,
});
//#endregion
</script>

<template>
  <div
    :class="[
      'ui-textarea',
      {
        'ui-textarea_error': currentError || isError,
        'ui-textarea_readonly': readonly,
      }
    ]"
  >
    <UiLabel
      v-if="label"
      :font-size="labelSize"
      :bold="labelBold"
      :label="label"
      :disabled="disabled"
      :required="required"
      class="t:mb-8"
    />
    <div class="t:relative">
      <ElInput
        ref="textareaRef"
        v-model="modelValue"
        class="ui-textarea__input"
        :class="{'ui-textarea__input_clear': isClear}"
        :disabled="disabled"
        :placeholder="placeholder"
        :rows="rows"
        :resize="resize"
        :autosize="autosize"
        :readonly="readonly"
        type="textarea"
        @blur="handleBlur"
        @focus="handleFocus"
      />
      <UiButton
        v-if="isCopy"
        v-clipboard="modelValue"
        v-clipboard:success="onCopy"
        v-clipboard:error="onError"
        icon="copy"
        is-icon
        class="ui-textarea__copy"
      />
      <ActionIcon
        v-if="isClear && modelValue"
        color="var(--text-negative)"
        :size="16"
        substyle="fab"
        icon="circle-xmark"
        custom-class="ui-textarea__clear"
        :is-overlay="false"
        @click="modelValue = ''"
      />
    </div>
    <UiHelp
      v-if="currentError"
      is-error
      :text="currentError"
    />
  </div>
</template>

<style lang="scss">
.ui-textarea{
  position: relative;
  &__input {
    --el-font-size-base: 13px;
    --el-input-text-color: var(--text-body);
    --el-input-border-color: var(--border-default);
    --el-input-hover-border: var(--border-default);
    --el-input-focus-border-color: var(--border-focused);
    --el-input-border-radius: 5px;
    --el-input-placeholder-color: var(--text-body-tertiary);
    --el-input-bg-color: var(--background-body);
    .el-textarea__inner {
      padding: 12px 12px 8px;
      font-family: var(--main-font);
      line-height: normal;
      font-weight: 400;
    }
    &_clear {
      .el-textarea__inner {
        padding-right: 40px;
      }
    }
  }
  &__copy {
    position: absolute;
    right: 6px;
    top: 6px;
    z-index: 10;
  }
  &__clear {
    position: absolute;
    top: 12px;
    right: 12px;
  }

  &_error &{
    &__input {
      --el-input-border-color: var(--border-negative);
      --el-input-hover-border-color: var(--border-negative);
      //--el-input-focus-border-color: var(--border-negative);
    }
  }
  &_readonly &{
    &__input {
      --el-input-bg-color: #f4f7fa;
    }
  }
}
</style>
